import { useEffect } from 'react';

/**
 * Watches the DOM for MUI inline error <Alert> nodes appearing anywhere
 * in the page and scrolls them into view. Solves the common form-page
 * UX bug where validation errors render above (or below) the current
 * scroll position and the user is left staring at an unchanged form.
 *
 * Scoped to the MUI Alert "error" classes so toasts and non-error alerts
 * aren't affected.
 *
 * Mount once near the app root (sibling of <Router />).
 */
const ALERT_SELECTOR = '.MuiAlert-root.MuiAlert-standardError, .MuiAlert-root.MuiAlert-outlinedError, .MuiAlert-root.MuiAlert-filledError';

const isInViewport = (el) => {
    const r = el.getBoundingClientRect();
    return r.top >= 0 && r.bottom <= (window.innerHeight || document.documentElement.clientHeight);
};

const scrollAlertIntoView = (el) => {
    if (!el || isInViewport(el)) return;
    try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
        el.scrollIntoView();
    }
};

const AutoScrollError = () => {
    useEffect(() => {
        const seen = new WeakSet();

        // Initial pass — handle any error alert already on the page when
        // we mount (e.g. first render after a server-side validation).
        document.querySelectorAll(ALERT_SELECTOR).forEach((el) => {
            seen.add(el);
            scrollAlertIntoView(el);
        });

        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                m.addedNodes.forEach((n) => {
                    if (n.nodeType !== 1) return;
                    const matches = n.matches?.(ALERT_SELECTOR)
                        ? [n]
                        : Array.from(n.querySelectorAll?.(ALERT_SELECTOR) || []);
                    for (const el of matches) {
                        if (seen.has(el)) continue;
                        seen.add(el);
                        // Defer one frame so layout has settled before we measure.
                        requestAnimationFrame(() => scrollAlertIntoView(el));
                    }
                });
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    return null;
};

export default AutoScrollError;
